import { Injectable } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/database';
import { AuthService } from './auth.service';
import { Observable, BehaviorSubject, Subscription, interval } from 'rxjs';
import { filter, first, map, timeout, switchMap, startWith } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { environment } from 'environments/environment';
import { Account } from '@models/Account';

@Injectable({
  providedIn: 'root'
})
export class AccountService {


  static licenseTypes = [{id: 'concurrent_user_based', name: 'Concurrent User Based'}, {id: 'expert_user_based', name: 'Expert User Based'}];
  static licenseModels = [{id: 'starter', name: 'Starter'}, {id: 'professional', name: 'Professional'}, {id: 'ultimate', name: 'Ultimate'}, {id: 'enterprise', name: 'Enterprise'}];

  static allFeatures = [{id: 'snapshot', name: 'Take Snapshot'}, {id: 'imagecollaboration', name: 'Image Collaboration'}, {id: 'downloadimgcoll', name: 'Download Image Collaboration'},
    {id: 'pdfcollaboration', name: 'Pdf Collaboration'}, {id: 'arcollaboration', name: 'Ar Collaboration'}, {id: 'weeklyreport', name: 'Weekly Usage Report'},
    {id: 'filesharing', name: 'File Sharing'}, {id: 'reactions', name: 'Reactions'}, {id: 'joinwithlink', name: 'Join with Link'},

    {id: 'screensharing', name: 'Screen Sharing'}, {id: 'smartglass', name: 'Allow Smartglass'}, {id: 'threedcollaboration', name: 'Object Collaboration'},
    {id: 'chattranslation', name: 'Chat Translation'}, {id: 'cameracontrols', name: 'Zoom/Flash Controls'}, {id: 'batterystatus', name: 'Battery Status'},
    {id: 'schedulemeeting', name: 'Meeting Schedule'}, {id: 'roomfiles', name: 'Room Files Section'}, {id: 'adminreportcharts', name: 'Admin Panel Reports'},

    {id: 'workflowqr', name: 'Workflow QR (Easy Access)'}, {id: 'workflowai', name: 'Workflow AI'},

    {id: 'archiving', name: 'Archiving'}, {id: 'arplus', name: 'Ar Plus'}, {id: 'azureintegration', name: 'Azure Storage Integration'}, { id: 'coadmin', name: 'Allow Co-Admin' },
    {id: 'peertopeer', name: 'Peer to Peer'}, {id: 'twofactorauth', name: 'Two Factor Auth'}, {id: 'calling', name: 'Calling'}];

  static allAddOns = [{id: 'workflowconnections', name: 'Workflow Connections'}, {id: 'workflowsections', name: 'Workflow Sections'}, {id: 'workflowremoteintegration', name: 'Workflow Remote Integration'}, {id: 'nestedworkflows', name: 'Nested Workflows'},
    {id: 'mobiledeeplink', name: 'Mobile Deep Link URL & QR'}, {id: 'tubitakfeatures', name: 'Tubitak Features'}, {id: 'videoflow', name: 'Video Flow'}, {id: 'aiassistant', name: 'Show Nova AI'}, {id: 'workorder', name: 'Workorder'}, {id: 'trainingroom', name: 'Academy Room'}, {id: 'sessionexport', name: 'Session Export'},
    {id: 'sessionfilesaftersession', name: 'Session FilesAfterSession'}, {id: 'customsessionexportsubject', name: 'Custom Session Export Subject'}, {id: 'ticket', name: 'Ticket'}, {id: 'offlineticket', name: 'Offline Ticket'},
    {id: 'mandatoryticket', name: 'Mandatory Ticket'}, {id: 'customserviceemail', name: 'Custom Service Email'}, {id: 'coadminexpert', name: 'CoAdmin as Expert'}, {id: 'autojoinroom', name: 'Auto-Join Room'}, {id: 'webdav', name: 'Webdav'},
    {id: 'cammicbypass', name: 'Cam/Mic Permission Bypass'}, {id: 'sso', name: 'Single Sign-On'}, {id: 'fullhd', name: 'Mobile FullHD Support'}, {id: 'detailedvideoquality', name: 'Show Detailed Video Quality'},
    {id: 'dontkickinactives', name: "Don't Kick Inactive Users"}, {id: 'smssending', name: 'SMS Sending'}, {id: 'multiloginwarning', name: 'Multi-login Warning'}, {id: 'agreement', name: 'Ford User Agreement'},
    {id: 'azureadintegration', name: 'Microsoft Entra ID Integration'}, {id: 'workflowonremote', name: 'Workflow on Remote'}, {id: 'roomfilesforsubs', name: 'Room Files for Subscribers'}, {id: 'webstatus', name: 'Web User Status Tracking'},
    {id: 'remotewebhooks', name: 'Remote Webhooks'}, {id: 'workflowwebhooks', name: 'Workflow Webhooks'}, {id: 'autoguestname', name: 'Auto Guest Name'}, {id: 'skiptutorialforfields', name: 'Skip Tutorial for Fields'},
    {id: 'showclientsessionrating', name: 'Show Client Session Rating'}, {id: 'archiveoptimization', name: 'Archive Cost Optimization'}, {id: 'endmeetingforall', name: 'End Meeting for All (At Normal Rooms)'}, {id: 'invalidatemeetinglink', name: 'Delete Meeting Link'},
    {id: 'oidcsso', name: 'Open ID Connect SSO'}, {id: 'realtimetranslation', name: 'Realtime Translation'}, {id: 'wipesessiondatabtn', name: 'Wipe Session Data Button'}, {id: 'twofactorauthexpert', name: 'Two Factor Auth for Experts'}, {id: 'personalroomlanding', name: 'Personal Room Landing'}
  ];

  static timezones = ["UTC-12", "UTC-11", "UTC-10", "UTC-9", "UTC-8", "UTC-7", "UTC-6", "UTC-5", "UTC-4", "UTC-3", "UTC-2", "UTC-1", "UTC",
    "UTC+1", "UTC+2", "UTC+3", "UTC+4", "UTC+5", "UTC+6", "UTC+7", "UTC+8", "UTC+9", "UTC+10", "UTC+11", "UTC+12", "UTC+13", "UTC+14"];

  accountsMeta: Observable<Account[]>;
  private accountsMetaSource = new BehaviorSubject<Account[]>(null);
  private accountsMetaSub: Subscription = null;

  accountsManagers: Observable<any[]>;
  private accountsManagersSource = new BehaviorSubject<any[]>(null);
  private accountsManagersSub: Subscription = null;

  constructor(
    private authService: AuthService,
    private afdb: AngularFireDatabase,
    private http: HttpClient
  ) {
    this.accountsManagers = this.accountsManagersSource.asObservable().pipe(filter(a => a !== null));
    this.accountsMeta = this.accountsMetaSource.asObservable().pipe(filter(a => a !== null));

    this.authService.on("login", (user) => {
      this.startListeningAccountsMeta(user);
      this.startListeningAccountsManagers(user);
    });
    this.authService.on("logout", () => {
      this.stopListeningAccountsMeta();
      this.stopListeningAccountsManagers();
    });
  }

  startListeningAccountsMeta(user: any) {
    if (this.accountsMetaSub) { this.accountsMetaSub.unsubscribe() }

    this.accountsMetaSub = this.afdb.list<Account>('accounts_meta').snapshotChanges(['child_added', 'child_removed', 'child_changed'])
    .pipe(
      map(accountMetaSnaps => accountMetaSnaps.map(accountSnap => {
        let account = accountSnap.payload.val();
        account.id = accountSnap.key;

        account.room_count = account.rooms ? Object.keys(account.rooms).length : 0;
        account.user_count = account.users ? Object.keys(account.users).length : 0;

        account.total_session_count = account.total_session_count ? account.total_session_count : 0;
        const d = account.total_session_duration ? account.total_session_duration : 0;

        account.session_duration_hours = Math.floor(d / 3600000);
        account.session_duration_minutes = Math.floor((d - (account.session_duration_hours * 3600000)) / 60000);
        return account;
      })),
      switchMap(accounts => {
        // check account licenses every minute
        return interval(60000).pipe(startWith(-1), map(() => accounts.map(account => {
          const now = (new Date()).getTime();
          const twelveHoursInMs = 1000*60*60*12;
  
          account.no_remote_license = !account.license.remote_account || (account.license.remote_start - twelveHoursInMs > now) || (account.license.remote_end + twelveHoursInMs < now);
          account.no_workflow_license = !account.license.workflow_account || (account.license.workflow_start - twelveHoursInMs > now) || (account.license.workflow_end + twelveHoursInMs < now);
          account.no_license = account.no_remote_license && account.no_workflow_license;
  
          return account;
        })))
      })
    )
    .subscribe(meta => {
      this.accountsMetaSource.next(meta);
    });
  } 

  startListeningAccountsManagers(user) {
    if (this.accountsManagersSub) { this.accountsManagersSub.unsubscribe() }

    this.accountsManagersSub = this.afdb.list<any>('account_managers').valueChanges()
    .subscribe(managers => this.accountsManagersSource.next(managers));
  }

  stopListeningAccountsMeta() {
    if (this.accountsMetaSub) { this.accountsMetaSub.unsubscribe() }
    this.accountsMetaSource.next(null);
  }

  stopListeningAccountsManagers() {
    if (this.accountsManagersSub) { this.accountsManagersSub.unsubscribe() }
    this.accountsManagersSource.next(null);
  }

  createAccount(newAccount: any, customer_emails: string[]) {
    const body: any = { token: this.authService.currentUser.token, account: newAccount, customer_emails: customer_emails };
    return this.http.post(environment.endPoints.createaccount, body).pipe(first()).toPromise()
    .then((response: any) => {
      return response.result;
    });
  }

  updateAccount(changes): Promise<void> {
    return this.http.post(environment.endPoints.updateaccount, { token: this.authService.currentUser.token, changes: changes }).pipe(first()).toPromise()
    .then(async(result) => {
      return (await new Promise<void>(resolve => {
        const sub = this.accountsMeta.pipe(
          timeout(5000),
          map(accounts => accounts ? accounts.find(account => account.id === changes.accountId) : null)
        ).subscribe(
          account => {
            if (account) {
              if (changes.accountManager && account.account_manager != changes.accountManager) {
                return
              }
              if (changes.companyName && account.company_name != changes.companyName) {
                return
              }
              if ((changes.adminMail || changes.adminMail === '') && account.admin.email != changes.adminEmail) {
                return
              }
              if (changes.timezone && account.timezone != changes.timezone) {
                return
              }
              if (changes.license) {
                if (changes.license.type && changes.license.type !== account.license.type) {
                  return
                }
                if (changes.license.start && changes.license.start !== this.formatLicenseDate(new Date((new Date(account.license.start)).toLocaleString("en-US", {timeZone: "America/Los_Angeles"})))) {
                  return
                }
                if (changes.license.end && changes.license.end !== this.formatLicenseDate(new Date((new Date(account.license.end)).toLocaleString("en-US", {timeZone: "America/Los_Angeles"})))) {
                  return
                }
                if (changes.license.model && changes.license.model !== account.license.model) {
                  return
                }
                if ((changes.license.demo === true || changes.license.demo === false ) && changes.license.demo !== account.license.demo) {
                  return
                }
                if (changes.license.account_limit && parseInt(changes.license.account_limit) !== account.license.limit) {
                  return
                }
                if (changes.license.room_concurrent_limit && parseInt(changes.license.room_concurrent_limit) !== account.license.limit) {
                  return
                }
                if (changes.license.expert_limit && parseInt(changes.license.expert_limit) !== account.license.limit2) {
                  return
                }
                if (changes.license.expert_concurrent_limit && parseInt(changes.license.expert_concurrent_limit) !== account.license.expert_concurrent_limit) {
                  return
                }
                if (changes.license.room_limit && parseInt(changes.license.room_limit) !== account.license.room_limit) {
                  return
                }
                if (changes.license.ultimate_time && parseInt(changes.license.ultimate_time) !== account.license.ultimate_time) {
                  return
                }
              }
              if (changes.features) {
                if (changes.license && changes.license.ultimate_time) {
                  if (parseInt(changes.license.ultimate_time) > 0) {
                    if (!account.license.ultimate_trial) {
                      return
                    }
                  } else {
                    if (account.license.ultimate_trial) {
                      return
                    }
                  }
                }
                for (const key of Object.keys(changes.features)) {
                  if (changes.features[key] !== (account.license.ultimate_trial ? account.after_trial_features[key] : account.features[key])) {
                    return
                  }
                }
              }
              if (changes.addOns) {
                for (const key of Object.keys(changes.addOns)) {
                  if (changes.addOns[key] !== account.add_ons[key]) {
                    return
                  }
                }
              }
              if (sub) { sub.unsubscribe() }
              resolve();
            }
          }, error => {
            resolve();
          }
        );
      }));
    });
  }

  changeAdminPassword(accountId: string, newPassword: string): Promise<any> {
    return this.http.post(environment.endPoints.updateaccount, { token: this.authService.currentUser.token, changes: {accountId: accountId, password: newPassword} }).pipe(first()).toPromise()
  }

  changeArchiveStorageLimits(accountId: string, dedicatedArchive: number, expireArchive: number) {
    const storageLimits = {dedicatedArchive: dedicatedArchive.toString(), expireArchive: expireArchive.toString()};
    return this.http.post(environment.endPoints.updateaccount, { token: this.authService.currentUser.token,
      changes: {accountId: accountId, storageLimits: storageLimits} }).pipe(first()).toPromise()
  }

  getAzureStorageData(accountId: string) {
    return this.afdb.object<any>(`third_parties/accounts/${accountId}/azure`).valueChanges().pipe(first()).toPromise();
  }

  toggleAccountStatus(accountId: string): Promise<boolean> {
    return this.http.post(environment.endPoints.toggleaccount, { token: this.authService.currentUser.token, account: accountId }).pipe(first()).toPromise()
    .then(async(result: any) => {
      return (await new Promise<boolean>(resolve => {
        const sub = this.accountsMeta.pipe(
          timeout(5000),
          map(accounts => accounts ? accounts.find(account => account.id === accountId) : null)
        ).subscribe(
          account => {
            if (account) {
              if (!account.disabled == !result.disabled) {
                if (sub) { sub.unsubscribe() }
                resolve(result.disabled);
              }
            }
          }, error => {
            resolve(result.disabled);
          }
        );
      }));
    });
  }

  deleteAccount(accountId) {
    return this.http.post(environment.endPoints.deleteaccount, { token: this.authService.currentUser.token, accountId: accountId }).pipe(first()).toPromise();
  }

  wipeAccount(accountId) {
    return this.http.post(environment.endPoints.wipeaccount, { token: this.authService.currentUser.token, account: accountId }).pipe(first()).toPromise();
  }

  getAccount(account_id: string) {
    return this.accountsMeta.pipe(
      first(),
      map(accountsMeta => accountsMeta ? accountsMeta.find(account => account.id === account_id) : null)
    ).toPromise();
  }

  formatLicenseDate(date: Date) {
    return date.getFullYear() + '-' + ("0"+(date.getMonth()+1)).slice(-2) + '-' + ("0" + date.getDate()).slice(-2);
  }

  resendSessionExport(accountId: string, roomId: string, sessionId: string, recipients: string[]) {
    return this.http.post(environment.endPoints.exportsession, {
      token: this.authService.currentUser.token,
      account: accountId,
      room: roomId,
      session: sessionId,
      from_console: true,
      recipients: recipients
    }).toPromise();
  }

  copyDemoWorkflow(targetAccountId: string, name: string, sourceAccountId?: string, sourceWorkflowId?: string) {
    const payload: any = {
      token: this.authService.currentUser.token,
      targetAccountId: targetAccountId
    };

    if (sourceAccountId && sourceWorkflowId) {
      // Custom workflow copy
      payload.sourceAccountId = sourceAccountId;
      payload.sourceWorkflowId = sourceWorkflowId;
    } else if (environment.demoWorkflow[name]) {
      // Predefined demo workflow
      payload.sourceAccountId = environment.demoWorkflow[name].sourceAccountId;
      payload.sourceWorkflowId = environment.demoWorkflow[name].sourceWorkflowId;
    } else {
      return Promise.reject(new Error('Invalid workflow configuration'));
    }

    return this.http.post<any>(environment.endPoints.copyDemoWorkflow, payload).toPromise();
  }

  sendCreationEmail(accountId: string, emails: string[]) {
    return this.http.post(environment.endPoints.createaccountpdf, {
      token: this.authService.currentUser.token,
      accountId: accountId,
      emails: emails
    }).toPromise();
  }

  getManagerReport(emails: string[], accounts: string[], timezone: string, reportStartEnd: Date[]) {
    if (reportStartEnd.length > 0) {
      return this.http.post<any>(environment.endPoints.getManagerReport, {
          token: this.authService.currentUser.token,
          emails: emails,
          accounts: accounts,
          timezone: timezone,
          reportStartEnd: reportStartEnd.map(d => d.getTime())
        }).toPromise();
    } else {
      return this.http.post<any>(environment.endPoints.getManagerReport, {
          token: this.authService.currentUser.token,
          emails: emails,
          accounts: accounts,
          timezone: timezone
        }).toPromise();
    }
  }

  applyRemoteForceUpdate(forceUpdateInfo: any, accountIds: string[]) {
    return this.http.post<any>(environment.endPoints.applyRemoteForceUpdate, {
      token: this.authService.currentUser.token,
      accountIds: accountIds,
      forceUpdateInfo: forceUpdateInfo
    }).toPromise();
  }

  importUsersConfirm(accountId: string, accountName: string, formData: FormData) {
    console.log('importUsersConfirm', accountId, accountName);
    const token = this.authService.currentUser.token;
    const url = environment.endPoints.importusersconfirm+`?token=${token}&account_id=${accountId}`;
    return this.http.post<any>(url, formData).pipe(first()).toPromise()
      .then(result => {
        return result;
      })
  }

  importUsers(accountId: string, excelUploadUsers: any) {
    const token = this.authService.currentUser.token;
    const url = environment.endPoints.importusers;
    return this.http.post<any>(url, { excel_upload_users: excelUploadUsers, token: token, account_id: accountId }).pipe(first()).toPromise();
  }

  createClaroRooms(accountId: string, action: "check" | "create") {
    const token = this.authService.currentUser.token;
    const url = environment.endPoints.createClaroRooms;
    return this.http.post<any>(url, { token: token, accountId: accountId, action: action }).pipe(first()).toPromise();
  }

  getUsageStats(accounts: string[], timezone: string) {
    const token = this.authService.currentUser.token;
    const url = environment.endPoints.getUsageStats;
    return this.http.post<any>(url, { token: token, accounts: accounts, timezone: timezone }).pipe(first()).toPromise();
  }

  getSessionRatingsOfAccounts(accounts: string[]) {
    const token = this.authService.currentUser.token;
    const url = environment.endPoints.getSessionRatings;
    return this.http.post<any>(url, { token: token, accounts: accounts }).pipe(first()).toPromise();
  }

  getAllWorkflows(accountId: string) {
    const token = this.authService.currentUser.token;
    const url = environment.endPoints.getAllWorkflows;
    return this.http.post<any>(url, { token: token, accountId: accountId, returnLicense: true }).pipe(first()).toPromise();
  }
}
